/*
  vms-model-warm.js  (VMS, 2026-09-15)

  WHY. On a product page that carries a 3D model, the first tap on the "3D view"
  poster is slow because that tap is the FIRST time the browser fetches Shopify's
  model-viewer library: model-viewer-ui.en.js, model-viewer.js (v1.12) and
  vendors~mv.js, about 275 KB brotli in total. The measured tap to first frame is
  dominated by that fixed download (matrix reports/3d-speed-2026-09-14/
  LOAD-SPEED-MATRIX.md: about 4.5 s of the tap at Slow 4G and 1.8 s at Fast 4G is
  those three scripts). With the three already in cache the tap falls 3.4 to 4.8 s
  at Slow 4G and 1.1 to 1.4 s at Fast 4G on every page measured (PDS-LCHB 13.0 s to
  8.2 s, 11FC 4.8 s to 1.3 s). Page load itself is not affected by the scaffolding.

  OWNER RULING 2026-09-15, verbatim: "warm the library on model pages after load
  through Shopify's own feature loader at idle." No GLB is re-rendered; the only
  change is this theme-side warm-up.

  HOW THE LIBRARY LOADS (verified 2026-09-15 by reading the served scripts). There
  are TWO Shopify features, not one:
    - model-viewer-ui (v1.0): model-viewer-ui.en.js. This file only defines
      window.Shopify.ModelViewerUI; it contains NO Shopify.loadFeatures call and no
      reference to model-viewer.js, so requesting this feature alone caches only the
      12 KB UI script (measured: warm-up pulled 1 of 3, the tap still pulled 2).
    - model-viewer (v1.12): model-viewer.js, a webpack bundle whose publicPath is
      cdn.shopify.com/shopifycloud/model-viewer/v1.12/. On evaluation its runtime
      requests the split chunk vendors~mv.js (chunk map {1:"vendors~mv"}) from that
      path. So loading THIS feature pulls both model-viewer.js and vendors~mv.js.
  On the live poster tap, Dawn's product-model.js stamps the <template> into the DOM,
  the now-live <model-viewer data-shopify-feature="1.12"> element makes Shopify's own
  autoloader load the model-viewer feature, and product-model.js separately loads the
  model-viewer-ui feature. That is how all three arrive on tap.

  WHAT THIS DOES. After the window load event, at browser idle, it calls
  Shopify.loadFeatures for BOTH features so all three scripts land in cache:
    1. model-viewer-ui, version '1.0' (the same descriptor product-model.js uses).
    2. model-viewer, version read AT RUNTIME from the inert <model-viewer
       data-shopify-feature> attribute in the product-model <template> (never a
       pinned CDN url; MV_FALLBACK_VERSION below is used only if that attribute is
       ever missing, and must track the theme's model-viewer version if it changes).
  It reads that attribute from the inert template, so it does NOT create a
  <model-viewer> element, does NOT touch the gallery, does NOT fetch any GLB or
  poster, and dispatches no event the theme listens to (verified: 0 live
  <model-viewer> elements, 0 GLB/poster requests). The poster tap still runs
  product-model.js's own loadContent unchanged, now served from cache.

  SAFETY. Once-only guard on window, everything wrapped in try/catch so a failure can
  never break the page, and skipped entirely for Data Saver users
  (navigator.connection.saveData; effectiveType is deliberately not gated on).
  requestIdleCallback with a 4000 ms timeout, and a 2500 ms setTimeout fallback where
  requestIdleCallback is missing (Safari). Gated in sections/vms-main-product.liquid
  by the section setting "warm_model_library" (default on) inside the first_3d_model
  guard, so it ships only on pages that carry a 3D model. ASCII only; no em or en dash.
*/
(function () {
  'use strict';

  if (window.__vmsModelWarm) {
    return;
  }
  var flag = { started: null, requested: null, ok: false, error: null, t: null, mvVersion: null, times: {} };
  window.__vmsModelWarm = flag;

  // Used ONLY if the inert <model-viewer data-shopify-feature> attribute is missing.
  // Current theme value read from that element on 2026-09-15 was '1.12'. If Shopify
  // bumps the model-viewer version in the theme, the runtime read below tracks it
  // automatically; keep this fallback in step with the theme as a belt-and-braces.
  var MV_FALLBACK_VERSION = '1.12';

  function readMvVersion() {
    try {
      var pm = document.querySelector('product-model');
      if (!pm) {
        return MV_FALLBACK_VERSION;
      }
      var tpl = pm.querySelector('template');
      var mv = tpl ? tpl.content.querySelector('model-viewer') : pm.querySelector('model-viewer');
      var v = mv ? mv.getAttribute('data-shopify-feature') : null;
      return (v && v.length) ? v : MV_FALLBACK_VERSION;
    } catch (e) {
      return MV_FALLBACK_VERSION;
    }
  }

  function warm() {
    try {
      if (flag.requested) {
        return;
      }
      flag.requested = performance.now();
      flag.times.requested = flag.requested;
      var mvVersion = readMvVersion();
      flag.mvVersion = mvVersion;

      var uiDone = false;
      var mvDone = false;
      var hadError = false;
      function settle() {
        if (uiDone && mvDone) {
          if (!hadError) {
            flag.ok = true;
          }
          flag.t = performance.now();
          flag.times.loaded = flag.t;
        }
      }

      Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: function (errors) {
            uiDone = true;
            if (errors) {
              hadError = true;
              flag.error = 'model-viewer-ui load error';
            }
            settle();
          }
        },
        {
          name: 'model-viewer',
          version: mvVersion,
          onLoad: function (errors) {
            mvDone = true;
            if (errors) {
              hadError = true;
              flag.error = 'model-viewer load error';
            }
            settle();
          }
        }
      ]);
    } catch (e) {
      flag.error = (e && e.message) ? e.message : String(e);
    }
  }

  function schedule() {
    try {
      flag.started = performance.now();
      flag.times.started = flag.started;
      if (window.requestIdleCallback) {
        window.requestIdleCallback(warm, { timeout: 4000 });
      } else {
        window.setTimeout(warm, 2500);
      }
    } catch (e) {
      flag.error = (e && e.message) ? e.message : String(e);
    }
  }

  try {
    if (!window.Shopify || typeof Shopify.loadFeatures !== 'function') {
      flag.error = 'no Shopify.loadFeatures';
      return;
    }
    var hasModel = !!(
      document.querySelector('product-model') ||
      document.querySelector('[id^="ProductJSON-"]')
    );
    if (!hasModel) {
      flag.error = 'no product-model on page';
      return;
    }
    var conn = navigator.connection;
    if (conn && conn.saveData === true) {
      flag.error = 'skipped: saveData';
      return;
    }

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  } catch (e) {
    flag.error = (e && e.message) ? e.message : String(e);
  }
})();
